import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ShieldOff, ShieldCheck, UserX, Calendar, CircleDot } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const DAY_NAMES = ["M", "T", "W", "T", "F", "S", "S"];

const StatusDot = ({ status }) => {
  const map = {
    green: "bg-[#39FF14] shadow-[0_0_8px_rgba(57,255,20,0.6)]",
    yellow: "bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.5)]",
    red: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]",
    off: "bg-transparent border border-neutral-800",
  };
  return <div className={`w-6 h-6 ${map[status] || map.off}`} data-status={status} />;
};

export default function TrafficLights() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [scheduleDays, setScheduleDays] = useState([0, 1, 2, 3, 4]);
  const [action, setAction] = useState(null); // {type, member}
  const [reason, setReason] = useState("");

  const load = async () => {
    const { data } = await api.get("/team/traffic-lights?days=14");
    setData(data);
    setScheduleDays(data.schedule_days || [0, 1, 2, 3, 4]);
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (i) => setScheduleDays((s) => s.includes(i) ? s.filter((x) => x !== i) : [...s, i].sort());

  const saveSchedule = async () => {
    await api.post("/team/schedule", { schedule_days: scheduleDays });
    toast.success("Work schedule updated");
    load();
  };

  const performAction = async () => {
    if (!action) return;
    const { type, member } = action;
    try {
      await api.post(`/team/members/${member.user_id}/${type}`, { reason });
      toast.success(`${member.name} — ${type}`);
      setAction(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="p-10 text-neutral-500">Loading…</div>;
  const leader = user?.role === "team_leader";

  return (
    <div className="p-8 md:p-10 max-w-[1500px]">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// Traffic Light Monitor</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">
          <span className="text-[#39FF14]">Green</span> · <span className="text-yellow-400">Yellow</span> · <span className="text-red-500">Red</span>
        </h1>
        <p className="text-sm text-neutral-400 mt-3 max-w-2xl">
          Overlay of each member's daily KPI status on your team's work schedule.
          <span className="text-[#39FF14]"> Green</span> = complete ·
          <span className="text-yellow-400"> Yellow</span> = started ·
          <span className="text-red-500"> Red</span> = missed workday ·
          <span className="text-neutral-500"> Empty</span> = off-schedule.
        </p>
      </div>

      {/* Schedule editor (leader only) */}
      {leader && (
        <div className="hairline bg-[#0F0F0F] p-5 mb-4 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-[#39FF14]" />
            <span className="text-[10px] uppercase tracking-widest text-neutral-400">Work schedule</span>
          </div>
          <div className="flex gap-1">
            {DAY_NAMES.map((n, i) => (
              <button
                key={`day-${i}`}
                data-testid={`schedule-day-${i}`}
                onClick={() => toggleDay(i)}
                className={`w-9 h-9 hairline text-xs font-display font-bold uppercase transition-colors ${
                  scheduleDays.includes(i) ? "bg-[#39FF14] text-black border-[#39FF14]" : "bg-transparent text-neutral-500"
                }`}
              >{n}</button>
            ))}
          </div>
          <Button
            data-testid="save-schedule-btn"
            onClick={saveSchedule}
            className="rounded-none bg-[#39FF14] text-black hover:bg-white h-9 text-xs uppercase tracking-widest"
          >Save schedule</Button>
        </div>
      )}

      {/* Members grid */}
      {data.members.length === 0 && (
        <div className="hairline bg-[#0F0F0F] p-10 text-center text-neutral-500 text-sm">
          No team members yet. Share your join code from the Team page.
        </div>
      )}
      <div className="space-y-3">
        {data.members.map((m) => (
          <div key={m.user_id} data-testid={`tl-row-${m.user_id}`} className="hairline bg-[#0F0F0F] p-5 flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-3 min-w-[220px]">
              {m.picture ? (
                <img src={m.picture} className="w-11 h-11 hairline object-cover" alt="" />
              ) : (
                <div className="w-11 h-11 bg-[#1a1a1a] hairline flex items-center justify-center font-bold">
                  {m.name[0]}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold truncate">{m.name}</div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                  <span className={m.access_status === "active" ? "text-neutral-500" : "text-red-400"}>
                    {m.access_status}
                  </span>
                  {m.consecutive_misses > 0 && (
                    <span className="text-red-400">· {m.consecutive_misses} miss streak</span>
                  )}
                </div>
              </div>
            </div>

            {/* Traffic lights row */}
            <div className="flex gap-1.5 flex-1 justify-center flex-wrap">
              {m.days.map((d) => (
                <div key={d.date} className="flex flex-col items-center gap-1" title={`${d.date} · ${d.status}`}>
                  <StatusDot status={d.status} />
                  <span className="text-[9px] font-mono text-neutral-600">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>

            {leader && m.user_id !== user.user_id && (
              <div className="flex gap-1">
                {m.access_status === "active" ? (
                  <Button
                    data-testid={`suspend-${m.user_id}`}
                    onClick={() => setAction({ type: "suspend", member: m })}
                    variant="outline"
                    className="rounded-none hairline bg-transparent hover:bg-[#1a0a00] hover:text-yellow-400 h-9 text-xs uppercase tracking-widest"
                  >
                    <ShieldOff size={13} className="mr-1" /> Suspend
                  </Button>
                ) : (
                  <Button
                    data-testid={`unsuspend-${m.user_id}`}
                    onClick={() => setAction({ type: "unsuspend", member: m })}
                    variant="outline"
                    className="rounded-none hairline bg-transparent hover:bg-[#0a1a0a] hover:text-[#39FF14] h-9 text-xs uppercase tracking-widest"
                  >
                    <ShieldCheck size={13} className="mr-1" /> Restore
                  </Button>
                )}
                <Button
                  data-testid={`terminate-${m.user_id}`}
                  onClick={() => setAction({ type: "terminate", member: m })}
                  variant="outline"
                  className="rounded-none hairline bg-transparent hover:bg-[#1a0000] hover:text-red-400 h-9 text-xs uppercase tracking-widest"
                >
                  <UserX size={13} />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Action confirmation dialog */}
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setReason(""); } }}>
        <DialogContent data-testid="access-action-dialog" className="rounded-none bg-[#0F0F0F] hairline max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-widest text-sm">
              {action?.type === "suspend" && `Suspend ${action?.member?.name}?`}
              {action?.type === "unsuspend" && `Restore access for ${action?.member?.name}?`}
              {action?.type === "terminate" && `Terminate ${action?.member?.name}?`}
            </DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              {action?.type === "suspend" && "They will not be able to log KPIs until you restore access."}
              {action?.type === "unsuspend" && "They will regain full KPI access."}
              {action?.type === "terminate" && "They will be removed from the team permanently. This cannot be undone from here."}
            </DialogDescription>
          </DialogHeader>
          {action?.type !== "unsuspend" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">
                Reason (they will see this)
              </label>
              <Textarea
                data-testid="access-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Missed 5 consecutive days. Let's meet to discuss."
                className="rounded-none bg-[#0A0A0A] hairline min-h-[80px]"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} className="rounded-none hairline bg-transparent">Cancel</Button>
            <Button
              data-testid="confirm-access-action"
              onClick={performAction}
              className={`rounded-none uppercase tracking-widest text-xs ${
                action?.type === "terminate" ? "bg-red-500 text-white hover:bg-red-400" : "bg-[#39FF14] text-black hover:bg-white"
              }`}
            >Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
